import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { PromoProductTypes, getMyVouchers, getProductTypeOptions, postMyVouchers } from '~/services/PromoService';

import usePromoMeta from '../hooks/usePromoMeta';

const DEFAULT_DISCOUNT_REQUEST_JSON: Omit<definitions['Discount Request'], 'userId'> = {
  requestSource: 'cart',
  includeFullDetails: true,
  order: {
    deviceType: 'web',
    hasBedbankPromotion: false,
    isDepositOrder: false,
    loyaltyItems: [],
    isGiftOrder: false,
    clientOrderVersion: 1,
    status: 'active',
    region: 'AU',
    brand: 'luxuryescapes',
    items: [],
  },
};

type DiscountRequestItem = definitions['Discount Request']['order']['items'][number];

type EditableItem = {
  categoryBK: string;
  subCategoryBK: string;
  discountableTotal: string;
  offerId: string;
  itemId: string;
  mx: string;
  loyaltyOfferType: string;
  cartItemId: string;
  isMemberPrice: boolean;
  luxPlusPrice: string;
  provider: string;
};

type MyVouchersPostResult = {
  result?: {
    vouchers?: Array<{
      voucherId?: string;
      voucherName?: string;
      voucherTitle?: string;
      evaluations?: unknown;
    }>;
    voucherConditionResults?: unknown;
  };
};

const DEFAULT_ITEM: EditableItem = {
  categoryBK: 'hotel',
  subCategoryBK: '',
  discountableTotal: '100',
  offerId: '123',
  itemId: '123',
  mx: '123',
  loyaltyOfferType: '123',
  cartItemId: '123',
  isMemberPrice: false,
  luxPlusPrice: '100',
  provider: '123',
};

function getDefaultRequestJsonString(brand: string): string {
  return JSON.stringify(
    {
      ...DEFAULT_DISCOUNT_REQUEST_JSON,
      order: { ...DEFAULT_DISCOUNT_REQUEST_JSON.order, items: [], brand },
    },
    null,
    2,
  );
}

interface MyVouchersTesterProps {
  tenant: App.Tenant;
  /** Customer user id from the loaded promo tester order (id_member) */
  orderCustomerUserId?: string;
}

function formatJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function getPostResult(response: unknown): MyVouchersPostResult['result'] | undefined {
  if (!response || typeof response !== 'object') {
    return undefined;
  }
  return (response as MyVouchersPostResult).result;
}

function getCategoryOptions(promoProductTypes: PromoProductTypes | undefined): Array<{ value: string; label: string }> {
  if (!promoProductTypes) {
    return [];
  }
  return promoProductTypes.map((ppt) => ({
    value: ppt.category_bk,
    label: ppt.categoryName || ppt.category_bk,
  }));
}

function getSubCategoryOptions(
  promoProductTypes: PromoProductTypes | undefined,
  categoryBK: string,
): Array<{ value: string; label: string }> {
  if (!promoProductTypes || !categoryBK) {
    return [];
  }
  const allOptions = getProductTypeOptions(promoProductTypes);
  return allOptions
    .filter((opt) => opt.category_bk === categoryBK)
    .map((opt) => ({
      value: opt.value,
      label: opt.label,
    }));
}

export default function MyVouchersTester({ tenant, orderCustomerUserId }: MyVouchersTesterProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { promoMeta, isPromoMetaLoading } = usePromoMeta();

  const [userId, setUserId] = useState('');

  const [getResponse, setGetResponse] = useState('');
  const [isGetLoading, setIsGetLoading] = useState(false);

  const [requestJsonString, setRequestJsonString] = useState(() => getDefaultRequestJsonString(tenant.brand));
  const [items, setItems] = useState<EditableItem[]>([DEFAULT_ITEM]);
  const [postResponse, setPostResponse] = useState('');
  const [postResult, setPostResult] = useState<MyVouchersPostResult['result']>();
  const [isPostLoading, setIsPostLoading] = useState(false);

  const trimmedUserId = useMemo(() => userId.trim(), [userId]);

  useEffect(() => {
    if (orderCustomerUserId) {
      setUserId(orderCustomerUserId);
    }
  }, [orderCustomerUserId]);

  useEffect(() => {
    setRequestJsonString((prev) => {
      try {
        const parsed = JSON.parse(prev) as { order?: { brand?: string } };
        if (parsed?.order && parsed.order.brand !== tenant.brand) {
          return JSON.stringify({ ...parsed, order: { ...parsed.order, brand: tenant.brand } }, null, 2);
        }
      } catch {
        return prev;
      }
      return prev;
    });
  }, [tenant.brand]);

  const categoryOptions = useMemo(
    () => getCategoryOptions(promoMeta?.promo_product_types),
    [promoMeta?.promo_product_types],
  );

  const handleGetMyVouchers = async () => {
    if (!trimmedUserId) {
      enqueueSnackbar('Enter a userId first.', { variant: 'error' });
      return;
    }

    setIsGetLoading(true);
    try {
      const response = await getMyVouchers({
        page: 1,
        limit: 10,
        userIdOverride: trimmedUserId,
        brand: tenant.brand,
        region: 'AU',
      });
      setGetResponse(formatJson(response));
    } catch (error) {
      setGetResponse(formatJson({ error: (error as Error).message }));
      enqueueSnackbar(`GET /my-vouchers failed: ${(error as Error).message}`, { variant: 'error' });
    } finally {
      setIsGetLoading(false);
    }
  };

  const handlePostMyVouchers = async () => {
    if (!trimmedUserId) {
      enqueueSnackbar('Enter a userId first.', { variant: 'error' });
      return;
    }

    let parsedRequest: Omit<definitions['Discount Request'], 'userId'>;
    try {
      parsedRequest = JSON.parse(requestJsonString) as Omit<definitions['Discount Request'], 'userId'>;
    } catch {
      enqueueSnackbar('Discount Request JSON is not valid.', { variant: 'error' });
      return;
    }

    const mappedItems: Array<DiscountRequestItem> = items.map((item) => ({
      categoryBK: item.categoryBK as DiscountRequestItem['categoryBK'],
      subCategoryBK: (item.subCategoryBK || undefined) as DiscountRequestItem['subCategoryBK'],
      discountableTotal: Number(item.discountableTotal || 0),
      offerId: item.offerId,
      itemId: item.itemId,
      mx: item.mx,
      loyaltyOfferType: item.loyaltyOfferType,
      cartItemId: item.cartItemId,
      isMemberPrice: item.isMemberPrice,
      luxPlusPrice: Number(item.luxPlusPrice || 0),
      provider: item.provider,
    }));

    const discountRequest: definitions['Discount Request'] = {
      ...parsedRequest,
      userId: trimmedUserId,
      order: {
        ...parsedRequest.order,
        items: mappedItems,
      },
    };

    setIsPostLoading(true);
    try {
      const response = await postMyVouchers({
        brand: discountRequest.order.brand,
        discountRequest,
        page: 1,
        limit: 10,
        region: discountRequest.order.region,
        usableVouchersOnly: false,
      });
      setPostResponse(formatJson(response));
      setPostResult(getPostResult(response));
    } catch (error) {
      setPostResponse(formatJson({ error: (error as Error).message }));
      setPostResult(undefined);
      enqueueSnackbar(`POST /my-vouchers failed: ${JSON.stringify(error) as unknown}`, { variant: 'error' });
    } finally {
      setIsPostLoading(false);
    }
  };

  const handleAddItem = () => {
    setItems((prev) => [...prev, { ...DEFAULT_ITEM }]);
  };

  const handleDeleteItem = (index: number) => {
    setItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleItemChange = <K extends keyof EditableItem>(index: number, key: K, value: EditableItem[K]) => {
    setItems((prev) =>
      prev.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }
        const updated = { ...item, [key]: value };
        // Reset subCategoryBK when categoryBK changes
        if (key === 'categoryBK') {
          updated.subCategoryBK = '';
        }
        return updated;
      }),
    );
  };

  const vouchersWithEvaluations = postResult?.vouchers?.filter((voucher) => voucher.evaluations !== undefined) ?? [];
  const voucherConditionResults = postResult?.voucherConditionResults;

  if (isPromoMetaLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading promo meta...
        </Typography>
      </Stack>
    );
  }

  return (
    <Box>
      <PageSubheader title="Test /my-vouchers for a user" />

      <Box mt={2}>
        <Typography variant="subtitle1" gutterBottom>
          1. User
        </Typography>
        <TextField
          label="userId"
          value={userId}
          onChange={(event) => setUserId(event.target.value)}
          fullWidth
          placeholder="e.g. 44d54db4-4bc2-4dcb-91b2-aaeeb19978f7"
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          2. GET /my-vouchers
        </Typography>
        <Stack spacing={2}>
          <Box>
            <Button variant="contained" color="primary" onClick={handleGetMyVouchers} disabled={isGetLoading}>
              {isGetLoading ? 'Loading...' : 'Run GET /my-vouchers'}
            </Button>
          </Box>
          <TextField
            label="GET response"
            value={getResponse}
            fullWidth
            multiline
            minRows={8}
            InputProps={{ readOnly: true }}
          />
        </Stack>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box mb={3}>
        <Typography variant="subtitle1" gutterBottom>
          3. POST /my-vouchers
        </Typography>
        <Stack spacing={2}>
          <Typography variant="subtitle2">Items</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>categoryBK</TableCell>
                  <TableCell>subCategoryBK</TableCell>
                  <TableCell>discountableTotal</TableCell>
                  <TableCell>offerId</TableCell>
                  <TableCell>itemId</TableCell>
                  <TableCell>mx</TableCell>
                  <TableCell>loyaltyOfferType</TableCell>
                  <TableCell>cartItemId</TableCell>
                  <TableCell>isMemberPrice</TableCell>
                  <TableCell>luxPlusPrice</TableCell>
                  <TableCell>provider</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => {
                  const subCategoryOptions = getSubCategoryOptions(promoMeta?.promo_product_types, item.categoryBK);
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          value={item.categoryBK}
                          onChange={(event) => handleItemChange(index, 'categoryBK', event.target.value)}
                          sx={{ minWidth: 130 }}
                        >
                          {categoryOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          value={item.subCategoryBK}
                          onChange={(event) => handleItemChange(index, 'subCategoryBK', event.target.value)}
                          sx={{ minWidth: 180 }}
                          disabled={subCategoryOptions.length === 0}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {subCategoryOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={item.discountableTotal}
                          onChange={(event) => handleItemChange(index, 'discountableTotal', event.target.value)}
                          sx={{ minWidth: 100 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.offerId}
                          onChange={(event) => handleItemChange(index, 'offerId', event.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.itemId}
                          onChange={(event) => handleItemChange(index, 'itemId', event.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.mx}
                          onChange={(event) => handleItemChange(index, 'mx', event.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.loyaltyOfferType}
                          onChange={(event) => handleItemChange(index, 'loyaltyOfferType', event.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.cartItemId}
                          onChange={(event) => handleItemChange(index, 'cartItemId', event.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={item.isMemberPrice}
                          onChange={(event) => handleItemChange(index, 'isMemberPrice', event.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={item.luxPlusPrice}
                          onChange={(event) => handleItemChange(index, 'luxPlusPrice', event.target.value)}
                          sx={{ minWidth: 100 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.provider}
                          onChange={(event) => handleItemChange(index, 'provider', event.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleDeleteItem(index)} disabled={items.length === 1}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box>
            <Button variant="text" startIcon={<AddIcon />} onClick={handleAddItem}>
              Add item
            </Button>
          </Box>

          <TextField
            label="Discount Request JSON (excluding userId and order.items)"
            value={requestJsonString}
            onChange={(event) => setRequestJsonString(event.target.value)}
            fullWidth
            multiline
            minRows={12}
          />
          <Box>
            <Button variant="contained" color="primary" onClick={handlePostMyVouchers} disabled={isPostLoading}>
              {isPostLoading ? 'Loading...' : 'Run POST /my-vouchers'}
            </Button>
          </Box>
          <TextField
            label="POST response"
            value={postResponse}
            fullWidth
            multiline
            minRows={8}
            InputProps={{ readOnly: true }}
          />
          {postResponse && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Voucher evaluations
                </Typography>
                {vouchersWithEvaluations.length > 0 ? (
                  <Stack spacing={1}>
                    {vouchersWithEvaluations.map((voucher) => (
                      <TextField
                        key={voucher.voucherId ?? voucher.voucherName ?? voucher.voucherTitle}
                        label={voucher.voucherName ?? voucher.voucherTitle ?? voucher.voucherId ?? 'Voucher'}
                        value={formatJson(voucher.evaluations)}
                        fullWidth
                        multiline
                        minRows={3}
                        InputProps={{ readOnly: true }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info">No voucher evaluations returned for this POST /my-vouchers run.</Alert>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  voucherConditionResults
                </Typography>
                {voucherConditionResults !== undefined ? (
                  <TextField
                    label="voucherConditionResults"
                    value={formatJson(voucherConditionResults)}
                    fullWidth
                    multiline
                    minRows={5}
                    InputProps={{ readOnly: true }}
                  />
                ) : (
                  <Alert severity="info">No voucherConditionResults returned for this POST /my-vouchers run.</Alert>
                )}
              </Box>
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
